import { today } from '@seen/core/dates'
import {
  medicationsPdfFilename,
  medicationsPdfTitle,
} from '@seen/core/medicationsPdfTitle'
import type { Participant } from '@seen/core/models/patients/participant'
import { useCallback, useState } from 'react'
import type { FaxPendingDocument } from '#src/components/fax/SendFaxModal.tsx'
import type { GroupedMedication } from '#src/hooks/useCurrentMedications.ts'
import { preloadPdfRenderer, printPdfBlob } from '#src/utils/printPdfBlob.ts'
import { toast } from '#src/utils/toast.ts'

// pdf-lib and its embedded fonts are ~400 KB minified; load them only when a
// medications PDF is built so they stay out of every app's entry chunk.
let medicationsPdfModule:
  | Promise<
      typeof import('#src/components/patient/detail-content/medicationsPdf.ts')
    >
  | undefined

function loadMedicationsPdf() {
  if (!medicationsPdfModule) {
    const load =
      import('#src/components/patient/detail-content/medicationsPdf.ts')
    medicationsPdfModule = load
    // Drop a failed load (e.g. a chunk removed by a deploy) so the next click retries.
    load.catch(() => {
      medicationsPdfModule = undefined
    })
  }
  return medicationsPdfModule
}

export interface MedicationsPrintFaxModalState {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialParticipantRef: string
  initialPendingDocument?: FaxPendingDocument
  isPreparingInitialDocument: boolean
}

interface UseMedicationsPrintFaxResult {
  isPrinting: boolean
  handlePrint: () => void
  handleFax: () => void
  menuOpen: boolean
  onMenuOpenChange: (open: boolean) => void
  dismissMenu: () => void
  faxModal: MedicationsPrintFaxModalState
}

export function useMedicationsPrintFax({
  participant,
  rxMedications,
  otcMedications,
}: {
  participant: Participant
  rxMedications: GroupedMedication[]
  otcMedications: GroupedMedication[]
}): UseMedicationsPrintFaxResult {
  const [faxOpen, setFaxOpen] = useState(false)
  const [faxDocument, setFaxDocument] = useState<FaxPendingDocument | null>(
    null
  )
  const [isPrinting, setIsPrinting] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  // Close before the print dialog or fax modal opens. Callers also unmount the
  // menu contents on close: those overlays cancel the exit animation, and the
  // menu otherwise stays on screen after the flow finishes.
  const dismissMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const onMenuOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open)
    if (open) {
      preloadPdfRenderer()
      loadMedicationsPdf().catch(() => {})
    }
  }, [])

  const buildDocument = useCallback(async (): Promise<FaxPendingDocument> => {
    const exportedOn = today()
    const title = medicationsPdfTitle(participant.name, exportedOn)
    const { medicationsPdf } = await loadMedicationsPdf()
    return {
      id: crypto.randomUUID(),
      title,
      filename: medicationsPdfFilename(participant.name, exportedOn),
      blob: await medicationsPdf({
        participant,
        title,
        rxMedications,
        otcMedications,
      }),
      date: exportedOn,
    }
  }, [participant, rxMedications, otcMedications])

  const handlePrint = useCallback(() => {
    dismissMenu()
    void (async () => {
      setIsPrinting(true)
      try {
        const file = await buildDocument()
        await printPdfBlob(file.blob, file.title)
      } catch {
        toast.error('Failed to print medications')
      } finally {
        setIsPrinting(false)
        dismissMenu()
      }
    })()
  }, [buildDocument, dismissMenu])

  const handleFax = useCallback(() => {
    dismissMenu()
    setFaxDocument(null)
    setFaxOpen(true)
    void (async () => {
      try {
        setFaxDocument(await buildDocument())
      } catch {
        toast.error('Failed to prepare medications PDF')
        setFaxOpen(false)
      }
    })()
  }, [buildDocument, dismissMenu])

  return {
    isPrinting,
    handlePrint,
    handleFax,
    menuOpen,
    onMenuOpenChange,
    dismissMenu,
    faxModal: {
      open: faxOpen,
      onOpenChange: (open) => {
        if (!open) {
          setFaxOpen(false)
          setFaxDocument(null)
          dismissMenu()
        }
      },
      initialParticipantRef: participant.reference.reference,
      initialPendingDocument: faxDocument ?? undefined,
      isPreparingInitialDocument: faxOpen && !faxDocument,
    },
  }
}
